import { useEffect, type KeyboardEvent, type RefObject } from "react";
import { focusElement, getFocusableElements, getFormControls } from "./focusable";
import { returnFocus } from "./focusReturn";

export interface FocusTrapOptions {
    /** Trap is live only while `true`. */
    readonly active: boolean;
    /**
     * Element to focus when the trap activates. Falls back to the first
     * `[data-autofocus]`, then the first form control, then the container.
     */
    readonly initialFocusRef?: RefObject<HTMLElement | null> | undefined;
    /** Restore focus to the element that was focused before activation. Default `true`. */
    readonly returnFocus?: boolean;
}

/**
 * Keep Tab and Shift+Tab inside `containerRef` while active. Attach the
 * returned `onKeyDown` to the container.
 *
 * Focus already inside the container at activation is left where it is, so
 * a child that focuses itself on mount wins. The container itself, not its
 * close button, takes focus when nothing asks for it: a button lit up on
 * open reads as a stray ring.
 */
export function useFocusTrap(
    containerRef: RefObject<HTMLElement | null>,
    { active, initialFocusRef, returnFocus: restore = true }: FocusTrapOptions,
): { onKeyDown: (event: KeyboardEvent<HTMLElement>) => void } {
    useEffect(() => {
        if (!active) return;
        const container = containerRef.current;
        if (container === null) return;

        const previouslyFocused =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;

        if (!container.contains(document.activeElement)) {
            const preferred = initialFocusRef?.current ?? null;
            const autofocus = container.querySelector<HTMLElement>("[data-autofocus]");
            if (!focusElement(preferred) && !focusElement(autofocus)) {
                const [firstControl] = getFormControls(container);
                if (!focusElement(firstControl)) {
                    container.focus({ preventScroll: true });
                }
            }
        }

        return () => {
            if (!restore || previouslyFocused === null) return;
            if (!previouslyFocused.isConnected) return;
            returnFocus(previouslyFocused);
        };
    }, [active, containerRef, initialFocusRef, restore]);

    const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
        if (event.key !== "Tab") return;
        const container = containerRef.current;
        if (container === null) return;
        const focusables = getFocusableElements(container);
        if (focusables.length === 0) {
            event.preventDefault();
            container.focus({ preventScroll: true });
            return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const current = document.activeElement;
        if (event.shiftKey) {
            if (current === first || current === container || !container.contains(current)) {
                event.preventDefault();
                focusElement(last);
            }
            return;
        }
        if (current === last || !container.contains(current)) {
            event.preventDefault();
            focusElement(first);
        }
    };

    return { onKeyDown };
}
